import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, register as apiRegister, getMe, logoutApi, appleLogin } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore token on mount
  useEffect(() => {
    const stored = localStorage.getItem('petlings_token');
    const storedUser = localStorage.getItem('petlings_user');
    if (stored && storedUser) {
      try {
        setToken(stored);
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('petlings_token');
        localStorage.removeItem('petlings_user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const res = await apiLogin(username, password);
      const { access_token, user: userData } = res.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('petlings_token', access_token);
      localStorage.setItem('petlings_user', JSON.stringify(userData));
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      return { success: false, error: message };
    }
  }, []);

  const register = useCallback(async (email, password, verificationCode) => {
    try {
      const res = await apiRegister(email, password, verificationCode);
      const { access_token, user: userData } = res.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('petlings_token', access_token);
      localStorage.setItem('petlings_user', JSON.stringify(userData));
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed.';
      return { success: false, error: message };
    }
  }, []);

  // Sign in with Apple: the identity token from Apple's popup, checked by the
  // backend, which returns a Petlings session like any other sign-in.
  const loginWithApple = useCallback(async ({ identityToken, nonce, fullName }) => {
    try {
      const res = await appleLogin(identityToken, nonce, fullName);
      const { access_token, user: userData } = res.data;
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('petlings_token', access_token);
      localStorage.setItem('petlings_user', JSON.stringify(userData));
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Sign in with Apple failed. Please try again.';
      return { success: false, error: message };
    }
  }, []);

  const loginWithToken = useCallback(async (jwtToken, userData = null) => {
    try {
      localStorage.setItem('petlings_token', jwtToken);
      setToken(jwtToken);

      if (userData) {
        // User data provided directly (e.g. from Microsoft OAuth response)
        setUser(userData);
        localStorage.setItem('petlings_user', JSON.stringify(userData));
      } else {
        // Fetch user data from /auth/me
        const res = await getMe();
        const fetchedUser = res.data;
        setUser(fetchedUser);
        localStorage.setItem('petlings_user', JSON.stringify(fetchedUser));
      }
      return { success: true };
    } catch (err) {
      localStorage.removeItem('petlings_token');
      setToken(null);
      const message = err.response?.data?.detail || 'Token validation failed.';
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('petlings_token');
    localStorage.removeItem('petlings_user');
    logoutApi().catch(() => {}); // clear httpOnly cookie on server
  }, []);

  const isAuthenticated = !!token && !!user;

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated, login, loginWithToken, loginWithApple, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;
